/**
 * The links that belong to somebody who is not staff, read from a URL.
 *
 * One list, read by App (which answers these before touching Auth) and by
 * main.tsx (which pins such a page to English before the first paint, ADR
 * 0325). A route added here is both answered and kept English; a route left
 * out is neither, so the two cannot fall out of step.
 */

import { colorSlugFromPath, countyReportFromPath } from "@roof-hawk/core";

import { crewPacketTokenFromPath } from "./features/crewPacket/CrewPacketPage";
import { intakeSlugFromPath } from "./features/intake/IntakePage";
import { certificateTokenFromPath } from "./features/present/CertificatePage";
import { changeOrderTokenFromPath } from "./features/present/ChangeOrderPage";
import { presentTokenFromPath } from "./features/present/PresentPage";
import { stormTokenFromPath } from "./features/storm/StormPage";

export interface PublicLinks {
  intakeSlug: string | null;
  colorSlug: string | null;
  crewPacketToken: string | null;
  presentToken: string | null;
  changeOrderToken: string | null;
  certificateToken: string | null;
  stormToken: string | null;
  countyReport: ReturnType<typeof countyReportFromPath>;
}

export function publicLinksFrom(pathname: string): PublicLinks {
  return {
    intakeSlug: intakeSlugFromPath(pathname),
    colorSlug: colorSlugFromPath(pathname),
    crewPacketToken: crewPacketTokenFromPath(pathname),
    presentToken: presentTokenFromPath(pathname),
    changeOrderToken: changeOrderTokenFromPath(pathname),
    certificateToken: certificateTokenFromPath(pathname),
    stormToken: stormTokenFromPath(pathname),
    countyReport: countyReportFromPath(pathname),
  };
}

/**
 * Is any of them set? The empty string is a real value for `/go` and
 * `/colors`, so those are compared against null.
 */
export function hasPublicLink(links: PublicLinks): boolean {
  return Boolean(
    links.presentToken ||
    links.changeOrderToken ||
    links.certificateToken ||
    links.stormToken ||
    links.countyReport ||
    links.intakeSlug !== null ||
    links.colorSlug !== null ||
    links.crewPacketToken,
  );
}

/** Does App answer this URL as a page for somebody who is not staff? */
export function isPublicRoute(pathname: string): boolean {
  return hasPublicLink(publicLinksFrom(pathname));
}
